import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'

import type {SessionOrigin} from './origin'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {Session, TaskCompute} from './session'
import type {APITaskArtifact} from './artifact'
import type {TaskEnvironment} from '@github-ui/agent-sessions-types/environment'

/**
 * Azure DevOps repository context attached to a task. Sibling to (not a
 * replacement for) the GitHub `repository` foreign key — Mission Control
 * still anchors every task to a GH repo today; this field carries the ADO
 * repo info for Agency-initiated tasks so the UI can display it.
 *
 * Display-only — ADO performs its own permission checks before the caller
 * supplies this context to Mission Control. See backend equivalent in
 * `copilot-mission-control/internal/session/sessionstore/azure_devops_repository_context.go`
 * and the design rationale in the github/github monolith's
 * `docs/agency-mission-control-ado-data-plan.md`.
 */
export interface AzureDevOpsRepositoryContext {
  url: string
  name?: string
  default_branch?: string
}

/**
 * Agent collaborator associated with a task.
 */
export interface APITaskAgentCollaborator {
  agent_id: number
  agent_task_id: string
  agent_type: string
  slug?: string
}

/**
 * Structured user object returned by the Task API.
 */
export interface APIUser {
  id: number
  login?: string
}

/**
 * Structured repository object returned by the Task API.
 */
export interface APIRepository {
  id: number
}

/**
 * Repository attached to a group; the grouped endpoint pre-hydrates name/owner
 * so the client can derive the `nwo` without a separate repo lookup.
 */
export interface APIGroupRepository {
  id: number
  name: string
  full_name: string
  owner: {login: string}
}

/**
 * A group in the `/agents/grouped-tasks` response. `repository` is omitted for the
 * repo-less group (tasks with no repository), which the server pins first.
 */
export interface APITaskGroup {
  group_type: string
  repository?: APIGroupRepository
  tasks: APIGroupedTask[]
}

/**
 * A task in a grouped-tasks response; omits per-task `repository`, which lives on
 * the enclosing {@link APITaskGroup}.
 */
export type APIGroupedTask = Omit<APITask, 'repository'>

/**
 * Task shape accepted at the transformation boundary: `repository` is optional so
 * both flat ({@link APITask}) and grouped ({@link APIGroupedTask}) tasks fit.
 */
export type APITaskInput = Omit<APITask, 'repository'> & {repository?: APIRepository}

/**
 * Response shape of the user-scoped, unpaginated `/agents/grouped-tasks` endpoint.
 */
export interface ListGroupedTasksResponse {
  groups: APITaskGroup[]
}

/**
 * Raw task format from the new task API.
 * This is the format returned directly from the API before any transformation.
 */
export interface APITask {
  id: string
  creator: APIUser
  creator_type?: 'user' | 'organization'
  owner: APIUser
  repository: APIRepository
  /** @deprecated Use `state` when copilot_task_api_github_rest_style is enabled */
  status: string
  state?: string
  session_count: number
  /** deprecated se updated_at when copilot_task_api_github_rest_style is enabled */
  last_updated_at?: string
  updated_at?: string
  created_at: string
  artifacts?: APITaskArtifact[]
  agent_collaborators?: APITaskAgentCollaborator[]
  user_collaborators?: APIUser[]
  name?: string
  archived_at?: string | null
  remote_steerable?: boolean
  stale_at?: string
  automation_id?: string | null
  automation_revision_id?: string | null
  event_type?: string | null
  event_identifiers?: string[] | null
  // custom_agent is deprecated
  custom_agent?: {
    name?: string
    is_automation?: boolean
  }
  sharing_status?: 'unshared' | 'shared'
  compute?: TaskCompute
  current_environment?: TaskEnvironment | null
  /**
   * Surface that originated this task. Optional and additive — see
   * packages/agent-sessions/types/origin.ts and
   * docs/agency-mission-control-crossover/process.md §7.2.
   */
  origin?: SessionOrigin
  /**
   * Azure DevOps repository context for Agency-initiated tasks running
   * against an ADO repo. Sibling to (not a replacement for) the GitHub
   * `repository` field. See AzureDevOpsRepositoryContext above.
   */
  azure_devops_repository_context?: AzureDevOpsRepositoryContext
}

/**
 * Session shape returned by the Task API when sessions are nested inside a task response.
 * Uses structured objects (`user`, `owner`, `repository`) instead of raw ID fields.
 */
export type TaskAPISession = Omit<Session, 'user_id' | 'owner_id' | 'repo_id'> & {
  user: APIUser
  owner: APIUser
  repository: APIRepository
}

/**
 * Raw hydrated task response from the Task API before normalization.
 * Sessions use the Task API field names (structured objects).
 */
export interface RawHydratedAPITask extends APITask {
  sessions: TaskAPISession[]
}

/**
 * Hydrated version of APITask that includes session data.
 * Used for the task detail page where session information is loaded.
 * Sessions are normalized to the standard Session type at the fetch boundary.
 */
export interface HydratedAPITask extends APITask {
  sessions: Session[]
}

/**
 * Normalizes a Task API session (with structured objects) to the standard Session type.
 * Strips the structured `user`, `owner`, and `repository` fields and replaces them with flat IDs.
 */
export function normalizeTaskAPISession(raw: TaskAPISession): Session {
  const {user, owner, repository, ...rest} = raw
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- Session has fields not always present on Task API payloads; this is a normalization boundary.
  return {
    ...rest,
    user_id: user?.id,
    user_login: user?.login,
    owner_id: owner?.id,
    repo_id: repository?.id,
  } as Session
}

/**
 * Normalizes a raw Task API response to a HydratedAPITask with standard Session objects.
 */
export function normalizeHydratedAPITask(raw: RawHydratedAPITask): HydratedAPITask {
  return {
    ...raw,
    sessions: (raw.sessions ?? []).map(normalizeTaskAPISession),
  }
}

/**
 * Normalizes a session that may use either the Task API shape (nested `user`, `owner`, `repository`)
 * or the legacy shape (flat `user_id`, `owner_id`, `repo_id`).
 *
 * Use at fetch boundaries where sessions may come from either shape (e.g. the task API
 * vs the legacy pull-sessions endpoint).
 */
export function maybeNormalizeSession(raw: TaskAPISession | Session): Session {
  if ('user' in raw && raw.user && typeof raw.user === 'object') {
    return normalizeTaskAPISession(raw)
  }
  return raw as Session
}

/**
 * Parameters for creating a task via the API.
 */
export interface CreateAPITaskParams {
  nwo: string
  problemStatement: string
  model?: string
  /** Maps to the CMC OpenAPI `reasoning_effort` request field. */
  reasoningEffort?: ReasoningEffort
  agentId?: string
  customAgent?: string
  baseRef?: string
  createPullRequest?: boolean
  mcpPermissionMode?: McpPermissionMode
  eventType?: string
  eventContent?: string
  eventIdentifiers?: string[]
}

/**
 * Parameters for the create task mutation, including client-generated ID for optimistic updates.
 */
export interface CreateAPITaskMutationParams extends CreateAPITaskParams {
  clientGeneratedId: string
  endpoint?: string
}
